import { Alert, Button as DesignSystemButton, Modal, Switch, useToast } from "@aidkitorg/component-library";
import * as survey from "@aidkitorg/types/lib/survey";
import { captureException } from "@sentry/react";
import { snakeCase } from 'lodash';
import Papa from "papaparse";
import React, { ReactNode, Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from 'react-router-dom';
import { usePost } from "./API";
import { ConfigurationContext, PublicConfigurationContext } from "./Context";
import { AidKitLogo, BUTTON_CLASS, SpacedSpinner } from "./Util";
import { Create } from "@aidkitorg/types/lib/survey";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

type ButtonProperties = {
  label: string,
  [key: string]: any
}
export function Button(props: ButtonProperties): JSX.Element {
  return <button {...props} className="block w-full tracking-wider rounded-md pt-3 pb-2 text-green-700 shadow-sm border-gray-300 bg-green-300 hover:text-green-100 hover:bg-green-400 font-logo text-2xl">{props.label}</button>
}

function utf8_to_b64(str: string) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

function b64_to_utf8(str: string) {
  return decodeURIComponent(escape(window.atob(str)));
}


function tryDecodingHash(hash: string): HashData | null {
  try {
    const decoded = JSON.parse(b64_to_utf8(hash));
    if (typeof decoded === 'object' && decoded !== null) {
      if (decoded?.columns) {
        return {
          config: decoded,
          groupByField: null,
          nameField: null,
          autoColumns: false,
        } as HashData;
      }
    }
    return decoded as HashData;
  } catch (e) {
    return null;
  }
}

function validateAddress(address: string, format: survey.AddressFormatting) {
  if (!address) {
    return {};
  };
  const address_parts = address.split(',');

  const errorMessage = { error: "Please provide address as: 123 Street, City, State ZipCode, Country" };

  let address1, address2, city, state_zip, country;
  if (address.includes('USA')) {
    if (address_parts.length === 4) {
      address2 = '';
      [address1, city, state_zip, country] = address_parts;
    } else if (address_parts.length === 5) {
      [address1, address2, city, state_zip, country] = address_parts;
    } else {
      return errorMessage;
    }
  } else {
    if (address_parts.length === 3) {
      address2 = '';
      [address1, city, state_zip] = address_parts;
    } else if (address_parts.length === 4) {
      [address1, address2, city, state_zip] = address_parts;
    } else {
      return errorMessage;
    }
  }

  // Allow for multiple spaces between the state and zip
  const [state, zip] = (state_zip as string).split(' ').filter(s => s.trim() !== '');
  console.log('state', state);
  console.log('zip', zip);

  // make sure zip code is 5 digits
  if (format.validateZipCode && (zip?.trim()?.length !== 5 || !/^\d+$/.test(zip))) {
    return { error: "Zip code must be 5 digits" };
  }

  // make sure state is valid state abbreviation
  if (format.validateStateAbbreviation && !['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 
    'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 
    'OH', 'OK', 'OR', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 
    'VA', 'WA', 'WV', 'WI','WY',].includes(state)) {
    return { error: "Invalid state abbreviation" };
  }

  return { address1, address2, city, state, zip, country };
}

interface HashData {
  config: survey.CSVMapping;
  groupByField: string | null;
  nameField: string | null;
  autoColumns: boolean;
  showDistroForNonAdmins: boolean;
}

export function ImportPage() {
  const configContext = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);

  const importCSV = usePost('/program/admin/import_csv');
  const importCSVStatuses = usePost('/program/admin/async_job_statuses');
  const createImportSignature = usePost('/program/admin/create_import_signature');
  const initialHash: HashData = React.useMemo(() => {
    return tryDecodingHash(window.location.hash.slice(1)) || {
      config: {
        mode: {
          kind: 'Update',
          uidColumn: ''
        },
        columns: [] as survey.CSVMapping['columns']
      },
      groupByField: null,
      nameField: null,
      autoColumns: false,
      showDistroForNonAdmins: false,
    };
  }, []); // Only calculate once on mount
  const defaultConfig = {
    mode: {
      kind: 'Update',
      uidColumn: ''
    },
    columns: [] as survey.CSVMapping['columns']
  };
  const [config, setConfig] = useState<survey.CSVMapping>(initialHash.config || defaultConfig);
  const [allowedFields, setAllowedFields] = useState('');
  const [generatedImportUrl, setGeneratedImportUrl] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [autoColumns, setAutoColumns] = useState<boolean>(initialHash.autoColumns || false);
  const [groupByField, setGroupByField] = useState<string | null>(initialHash.groupByField || null);
  const [nameField, setNameField] = useState<string | null>(initialHash.nameField || null);
  const [data, setData] = useState<Record<string, string>[] | null>(null);
  const [dataHeader, setDataHeader] = useState<string[]>([]);
  const [grouped, setGrouped] = useState(false);
  const [file, setFile] = useState<File>();
  const [importing, setImporting] = useState(false);
  const [taskIds, setTaskIds] = useState<string[]>([]);
  const taskIdToRowsRef = useRef<Record<string, Record<string, string>[]>>({});
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [completedRowCount, setCompletedRowCount] = useState(0);
  const [failedRows, setFailedRows] = useState<Record<string, string>[]>([]);
  const [serverValidationError, setServerValidationError] = useState('');
  const [showMapsToHeader, setShowMapsToHeader] = useState(false);

  // Default is true since that's historically what we do - but truly the usual 
  // behavior should be to hide the distro for non-admins.
  const [showDistroForNonAdmins, setShowDistroForNonAdmins] = useState(true);

  const [errors, setErrors] = useState<ErrorHolder>({});
  const [uniqueOrgNames, setUniqueOrgNames] = useState<string[]>([]);
  const [missingOrgNameRows, setMissingOrgNameRows] = useState<number[]>([]);
  const [formattedData, setFormattedData] = useState<Record<string, string | ReactNode>[]>([]);
  const [formattedColumns, setFormattedColumns] = useState<survey.CSVMapping['columns']>([] as survey.CSVMapping['columns']);
  const { toast } = useToast();

  useEffect(() => {
    // start out admins with the toggle off, since this is our recommendation.
    if ((configContext.roles || []).includes('admin')) {
      setShowDistroForNonAdmins(false);
      return;
    }

    // otherwise, use the initial hash value if it's explicitly set (including false).
    // this will allow older import links to continue showing the Distro if they were created before this change.
    if (initialHash.showDistroForNonAdmins !== undefined) {
      setShowDistroForNonAdmins(initialHash.showDistroForNonAdmins);
    }
  }, [configContext.roles]); // Remove initialHash from dependencies since it's now memoized

  const handleGenerateLink = useCallback(async () => {
    const url = new URL(window.location.href);
    url.searchParams.set('showDistroForNonAdmins', showDistroForNonAdmins.toString());
    const hash = url.hash;
    let generatedSignature: string | null = null;

    try {
      const import_signature = await createImportSignature(
        {allowedFields, hash: hash}
      );

      if (import_signature) {
        generatedSignature = import_signature;
        setGeneratedImportUrl(import_signature);
        setIsModalOpen(true);
        navigator.clipboard.writeText(import_signature);
      }
    } catch (e) {
      console.error('Error generating link: ', e);
    }
  }, [allowedFields, showDistroForNonAdmins]);

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedImportUrl)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000); // Reset copy status after 2 seconds
      })
      .catch((error) => console.error('Failed to copy text: ', error));
  };

    // This is just another way to say Record<string, Record<string, string[]>>
    type ErrorHolder = {
      [rowNumber: string]: {
        [columnIndex: string]: string[]
      }
    }
    const formatValue = (column: survey.CSVMapping['columns'][number], csvValue: string | Record<string, string>[], rowNumber: number, columnIndex: number, errors: ErrorHolder): { value: string | ReactNode, slugs?: Record<string, string> } => {
      const addError = (errorMessage: string) => {
        ((errors[rowNumber] ??= {})[columnIndex] ??= []).push(`Error in row ${rowNumber}, column "${column.csvColumn}" - ${errorMessage}`);
      }

      let value = csvValue;

      if (Array.isArray(value)) {
        // this means it's from a grouping and needs to be formated into a subtable for preview.
        const header = new Set(Object.keys(value[0]));
        for (const row of value) {
          const rowKeys = Object.keys(row);
          for (const key of rowKeys) {
            header.add(key);
          }
        }
        const headerArray = Array.from(header);
        const formatted = value.map(row => {
          return Object.fromEntries(headerArray.map(k => [k, row[k] || '']))
        })
        return { value: <table className="min-w-full bg-white border">
          <thead className="bg-gray-50 border-b">
            <tr>
              {headerArray.map((k, i) => <th key={i} className="px-4 py-2 text-left text-xs font-medium text-gray-500 lowercase tracking-wider">{k}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {formatted.map((row, rowIndex) => (
              <tr key={rowIndex} className="bg-white">
                {headerArray.map((k, i) => <td key={i} className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{row[k]}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        }
      }

      value = (value || '').trim();

      // Data validation and clean-up
      if (column.criteria === 'exists' && !value) {
        addError('Value must not be empty');
        return { value };
      }

      if (typeof column.criteria === 'object' && column.criteria?.kind === 'regex') {
        if (!(new RegExp(column.criteria.regex).test(value))) {
          addError(column.criteria.errorMessage);
          return { value };
        }
      }

      if (config.mode.kind === 'Update' && config.mode.uidColumn === column.csvColumn && !value) {
        addError('Missing UID');
        return { value };
      }

      if (config.mode.kind === 'Create' && config.mode.nameColumn === column.csvColumn && !value) {
        addError('Missing Name');
        return { value };
      }

      if (config.mode.kind === 'Create' && config.mode.hashedFromColumn === column.csvColumn && !value) {
        addError('Value must not be empty');
        return { value };
      }

      if (column.format === 'As Is') {
        // Do nothing, leave as is
      } else if (column.format === 'Phone') {
        const valueAllDigits = value.replace(/\D/g, '');
        value = value.replace(/(?!^\+)\D/g, '');
        if (value && (valueAllDigits.length < 10 || valueAllDigits.length > 13)) {
          addError('Phone must be between 10 and 13 digits');
        }
      } else if (column.format === 'Date') {
        value = value.split(/\/|-/).map((v) => v.padStart(2, '0')).join('/');
        
        // Validate that this is actually a valid date
        const dateObj = new Date(value);
        if (isNaN(dateObj.getTime())) {
          addError('Invalid date format');
        } else {
          // Additional check: make sure the parsed date matches the input
          // This catches cases like "13/45/2023" which would be parsed as a different date
          const formattedDate = dateObj.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit', 
            year: 'numeric'
          });
          if (formattedDate !== value) {
            addError('Invalid date');
          }
        }
      } else if (column.format === 'Email') {
        value = value.toLowerCase();
      } else if (typeof column.format === 'object' && 'options' in column.format) {
        let found = false;
        for (const option of column.format.options) {
          if (value === option.csvValue) {
            value = option.targetFieldValue;
            found = true;
          }
        }
        if (!found) {
          if (!column.format.allowUnknownValues) {
            addError('Unknown value');
          }
        }
      } else if (typeof column.format === 'object' && column.format?.kind === 'Address Formatting') {
        const parts = validateAddress(value, column.format);
        if ('error' in parts && parts.error) {
          addError(parts.error);
        }

        if (column.format.splitSeparateFields && 'address1' in parts) {
          return { value, slugs: {
            '_street': parts.address1 || '',
            '_street2': parts.address2 || '',
            '_city': parts.city || '',
            '_state': parts.state || '',
            '_zip': parts.zip || '',
            '_country': parts.country || '',
          }};
        } else {
          return { value };
        }
      } else if (typeof column.format === 'object') {
        let parsed;
        if (column.format.integersOnly) {
          parsed = parseInt(value);
          const parsedFloat = parseFloat(value);
          if (parsedFloat > parsed || parsedFloat < parsed){
            parsed = NaN;
          }
        } else {
          parsed = parseFloat(value);
        }

        if (isNaN(parsed)) {
          addError(`Non-${column.format.integersOnly ? 'integer' : 'number'} value found in column ${column.csvColumn}: ${value}`);
        }

        if (column.format.greaterThan !== undefined) {
          if (parsed <= column.format.greaterThan) {
            addError(`Error in column ${column.csvColumn}: ${value} is not greater than ${column.format.greaterThan}`);
          }
        }

        if (column.format.lessThan !== undefined) {
          if (parsed >= column.format.lessThan) {
            addError(`Error in column ${column.csvColumn}: ${value} is not less than ${column.format.lessThan}`);
          }
        }
      }

      return { value };
    };

    useEffect(() => {
      if (data) {
        const localErrors: ErrorHolder = {};

        // Build the list of columns that will be used for the import.
        // This allows slugs to be their own columns - specifically for addresses.
        let formattedColumns: survey.CSVMapping['columns'] = [];
        let longestRow = 0;

        // keep track of unique values. Key will be target_field,value
        let uniqueValues: Record<string, {rowIndex: number, value: string | ReactNode}[]> = {};

        // Build the formatted data
        const formatted = data.map((row, rowIndex) => {
          const rowData: Record<string, string | ReactNode> = {};

          if (config.mode.kind === 'Update' && config.mode.uidColumn) {
            rowData[config.mode.uidColumn] = row[config.mode.uidColumn];
          }

          const colsForRow: survey.CSVMapping['columns'] = [];
          let columnCount = 0;
          config.columns.forEach((column, columnIndex) => {
            colsForRow.push(column);

            const { value, slugs } = formatValue(column, row[column.csvColumn] || '', rowIndex, columnCount, localErrors);
            rowData[column.csvColumn] = value;

            if (column.unique) {
              const uniqueKey = `${column.targetField},${value}`;
              if (!uniqueValues[uniqueKey]) {
                uniqueValues[uniqueKey] = [];
              }
              uniqueValues[uniqueKey].push({rowIndex, value});
              if (uniqueValues[uniqueKey].length > 1) {

                // add error to all rows with the same value.
                uniqueValues[uniqueKey].forEach(v => {
                  localErrors[v.rowIndex] = localErrors[v.rowIndex] || {};
                  localErrors[v.rowIndex][columnIndex] = localErrors[v.rowIndex][columnIndex] || [];
                  localErrors[v.rowIndex][columnIndex] = [`Row ${v.rowIndex} - rows ${Array.from(uniqueValues[uniqueKey]).map(v => v.rowIndex).join(', ')} have the same value for column ${column.csvColumn}. Must be unique.`];
                });
              }
            }

            if (slugs) {
              Object.entries(slugs).forEach(([key, val]) => {
                rowData[column.csvColumn + key] = val;
                colsForRow.push({
                  ...column,
                  csvColumn: column.csvColumn + key,
                  format: 'As Is',
                  targetField: column.targetField + key,
                });
                columnCount++;
              });
            }
            columnCount++;
          });

          // The longest row (ie row with the most columns) determines the number of columns to display in the table.
          if (colsForRow.length > longestRow) {
            longestRow = colsForRow.length;
            formattedColumns = colsForRow;
          }

          return rowData;
        });

        setErrors(localErrors);
        setFormattedData(formatted);
        setFormattedColumns(formattedColumns);
      } else {
        setErrors({});
        setFormattedData([]);
        setFormattedColumns([]);
      }
    }, [data, config.columns]);

    const [previewing, setPreviewing] = useState(false);

    let distroRef = useRef<React.ComponentRef<typeof Distro>>(null);
    let refsLoaded = useRef(false);

    const transform = (entry: any, index: number) =>
      Object.fromEntries(
        Object.entries<any>({
          ...entry,
          legal_name: `Entry N${index}`
        }).map(
          ([k, v]) => ([snakeCase(k), v])
        )
      );

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawFile = e.target.files?.[0];
      if (rawFile) {
        const fileExtension = rawFile.name.split('.').pop();
        if (fileExtension === 'csv') {
          setData(null); // Necessary to allow uploading an updated file with the same name

          // Make sure we only have \n, no \r nor \r\n
          const fileText = await rawFile.text();
          const normalizedFileText = fileText.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
          const normalizedFile = new File([normalizedFileText], rawFile.name, { type: rawFile.type });

          setFile(normalizedFile);
        } else {
          alert('File must be a .csv\nUploaded file was .' + fileExtension);
          setFile(undefined);
          // This clears the input to prevent confusing behavior if the same file is uploaded twice in a row
          e.target.value = '';
        }
      }
    };

    useEffect(() => {
      if (file) {
        Papa.parse<any>(file, {
          header: true,
          skipEmptyLines: true,
          complete: function (results) {
            if (results.errors.length > 1) {
              alert(JSON.stringify(results.errors));
              setFile(undefined);
              return;
            }
            // Trim header keys in each row
            const trimmedData = results.data.map((row: Record<string, any>) => {
              return Object.fromEntries(
                Object.entries(row).map(([key, value]) => [key.trim(), value])
              );
            });

            setDataHeader(results.meta.fields ?? []);

            if (groupByField && nameField) {
              const map = trimmedData.reduce((prev: Map<string, any>, cur) => {
                if (!prev.has(cur[groupByField])) {
                  prev.set(cur[groupByField], [])
                }
                prev.get(cur[groupByField])!.push(cur);
                return prev;
              }, new Map<string, any[]>())

              setData(Array.from(map).map(
                ([k, v]) => ({
                  [groupByField]: k,
                  [nameField]: v[0][nameField],
                  Records: v.map(transform)
                })
              ));
              setGrouped(true);
            } else {
              setData(trimmedData);
            }
          },
        });
      }
    }, [file, groupByField, nameField]);

    useEffect(() => {
      if (data && data.length > 0) {
        // create key-value object to lookup existing column configurations
        const mappingsByKey: Record<string, survey.CSVMapping['columns'][number]> = {};
        for (const col of config.columns) {
          mappingsByKey[col.csvColumn] = col;
        }

        const newConfig: survey.CSVMapping = {
          mode: (grouped && nameField && groupByField) ? {
            kind: 'Create',
            nameColumn: nameField,
            hashedFromColumn: groupByField,
            orgNameColumn: (config.mode as Create).orgNameColumn,
          } : config.mode,
          columns: autoColumns ? Object.keys(data[0]).map(k => ({
            kind: 'FieldMapping',
            csvColumn: k,
            // try and get format existing config, fall back to 'As Is'
            format: mappingsByKey[k]?.format ?? 'As Is',
            targetField: nameField === k ? 'legal_name' : snakeCase(k),
            ...(mappingsByKey[k]?.criteria ? { criteria: mappingsByKey[k]?.criteria } : {}),
            ...(mappingsByKey[k]?.unique ? { unique: mappingsByKey[k]?.unique } : {}),
          })) : [...(config.columns ?? [])]
        }
        setConfig(newConfig);
        distroRef.current?.initialize(newConfig, undefined, undefined, undefined, {
          'csvColumns': Object.keys(data[0])
        });
      }
    }, [data, autoColumns]);

    // updating url hash is a side effect of config changes
    useEffect(() => {
      const newHash: HashData = {
        config,
        groupByField,
        nameField,
        autoColumns,
        showDistroForNonAdmins,
      };
      window.location.hash = '#' + utf8_to_b64(JSON.stringify(newHash));
    }, [config, groupByField, nameField, autoColumns, showDistroForNonAdmins]);

    const doImport = useCallback(async () => {
      setImporting(true);

      // Convert formatted data to string values for import
      const importData = formattedData.map(row => {
        const importRow: Record<string, string> = {};
        Object.entries(row).forEach(([key, value]) => {
          importRow[key] = typeof value === 'string' ? value : String(value);
        });
        return importRow;
      });

      const urlParams = new URLSearchParams(window.location.search);
      const taskResponse = (await importCSV({
        data: importData,
        mapping: config,
        import_signature: urlParams.get('import_signature') || '',
      }))
      const responseTaskIds: string[] = taskResponse?.taskIds ?? [];
      const responseRowCounts: number[] = taskResponse?.rowCounts ?? [];
      if (responseTaskIds.length > 0) {
        let offset = 0;
        const idToRows: Record<string, Record<string, string>[]> = {};
        for (let i = 0; i < responseTaskIds.length; i++) {
          const rowCount = responseRowCounts[i];
          idToRows[responseTaskIds[i]] = importData.slice(offset, offset + rowCount);
          offset += rowCount;
        }
        setTaskIds(responseTaskIds);
        taskIdToRowsRef.current = idToRows;
        setTotalRowCount(importData.length);
        setCompletedRowCount(0);
        setFailedRows([]);
      } else if (taskResponse.validationError) {
        setServerValidationError(taskResponse.validationError);
        setImporting(false);
      } else {
        setImporting(false);
        toast({
          description: "Import failed to enqueue",
          variant: "error"
        })
      }
    }, [formattedData, config]);

    const pollForImportFinish = useCallback(async () => {
      if (taskIds.length === 0) return;

      const statusResponse = await importCSVStatuses({ taskIds });
      const statuses: Record<string, string> = statusResponse.statuses || {};

      const stillPending: string[] = [];
      const newlyFailed: Record<string, string>[] = [];
      let newlyCompletedRows = 0;

      for (const id of taskIds) {
        const status = statuses[id];
        const batchRows = taskIdToRowsRef.current[id] || [];
        if (status === 'FINISHED') {
          newlyCompletedRows += batchRows.length;
        } else if (status === 'FAILED') {
          // count failed rows as completed so the progress counter reaches 100%
          newlyCompletedRows += batchRows.length;
          newlyFailed.push(...batchRows);
          console.log('async_import_failed: ' + id);
          captureException(new Error('Async import failed'), { extra: { taskId: id } });
        } else {
          stillPending.push(id);
        }
      }

      setTaskIds(stillPending);
      if (newlyCompletedRows > 0) setCompletedRowCount(prev => prev + newlyCompletedRows);
      if (newlyFailed.length > 0) setFailedRows(prev => [...prev, ...newlyFailed]);

      if (stillPending.length === 0) {
        setImporting(false);
        // failedRows = rows accumulated from prior poll cycles; newlyFailed = rows from this cycle
        // (setFailedRows above hasn't flushed yet, so we combine manually for the toast counts)
        const allFailed = [...failedRows, ...newlyFailed];
        const succeededCount = totalRowCount - allFailed.length;
        if (allFailed.length > 0) {
          // Don't close the preview on partial failure — the download button lives inside it
          toast({ description: `${succeededCount} rows imported successfully`, variant: "success" });
          toast({ description: `${allFailed.length} rows failed — download the failed rows CSV to retry.`, variant: "error" });
        } else {
          setPreviewing(false);
          toast({ description: `${totalRowCount} rows imported successfully`, variant: "success" });
        }
      }
    }, [taskIds, failedRows, totalRowCount, importCSVStatuses]);

    useEffect(() => {
      if (taskIds.length > 0) {
        const intervalId = setInterval(pollForImportFinish, 1000);
        return () => clearInterval(intervalId);
      }
    }, [taskIds, pollForImportFinish]);

    const downloadFailedRows = useCallback(() => {
      if (failedRows.length === 0) return;
      const headers = Object.keys(failedRows[0]);
      const csvLines = [
        headers.join(','),
        ...failedRows.map(row =>
          headers.map(h => `"${(row[h] ?? '').replace(/"/g, '""')}"`).join(',')
        )
      ];
      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'failed_import_rows.csv';
      a.click();
      URL.revokeObjectURL(url);
    }, [failedRows]);

    useEffect(() => {
      if (config.mode.kind === 'Create' && config.mode.orgNameColumn && data) {
        const { orgNameColumn } = config.mode;
        const uniqueNames: Set<string> = new Set();
        const missingRows: number[] = [];
        
        // Collect unique org names and rows missing an org name
        data.forEach((row, index) => {
          row[orgNameColumn] ? uniqueNames.add(row[orgNameColumn]) : missingRows.push(index);
        });
        const orgNamesArray = Array.from(uniqueNames);
        setUniqueOrgNames(orgNamesArray);
        // Missing org names will not prevent import but will be displayed as a warning
        setMissingOrgNameRows(missingRows);
      }
    }, [data, config]);

    const applicantFacingLogo = publicConfig.interface?.applicantFacingLogo?.url || configContext.applicant_facing_logo;
    const applicantFacingLogoWidth = publicConfig.interface?.applicantFacingLogo?.width || configContext.applicant_facing_logo_width || '50';
    const programName = publicConfig.name || configContext.program_name;

    // Error messages to guide users before clicking the Import Preview button
    let invalidMessage = '';
    if (!data || !file) {
      invalidMessage = 'Upload a csv to continue';
    } else if (config.mode.kind === 'Create') {
      if (!config.mode.nameColumn.length) {
        invalidMessage = 'Name column is required for creating new applicants';
      } else if (!dataHeader.some(col => col === (config.mode as survey.Create).nameColumn)) {
        invalidMessage = `The nameColumn "${config.mode.nameColumn}" does not match any column in your CSV. Please ensure that the nameColumn value matches exactly with the column containing names in your CSV file.`;
      }
      if (config.mode.orgNameColumn !== undefined && !dataHeader.some(col => col === (config.mode as survey.Create).orgNameColumn)) {
        invalidMessage = `The orgNameColumn "${config.mode.orgNameColumn}" does not match any column in your CSV. Please ensure that the orgNameColumn value matches exactly with the column containing organization names in your CSV file.`;
      }
    } else if (config.mode.kind === 'Update') {
      if (!config.mode.uidColumn) {
        invalidMessage = 'UID column is required for updating existing applicants';
      } else if (!dataHeader.some(col => col === (config.mode as survey.Update).uidColumn)) {
        invalidMessage = `The uidColumn "${config.mode.uidColumn}" does not match any column in your CSV. Please ensure that the uidColumn value matches exactly with the column containing UIDs in your CSV file.`;
      }
    }

    const hasErrors = Object.keys(errors).length > 0;

    return <div className="bg-gray-100 p-4 min-h-screen w-full">
      {/* banner to indicate which program */}
      <div className="import-banner -m-6 mb-4 px-4 py-2 bg-gray-200 flex gap-4 items-center border-gray-300 border-solid border-b border-l-0 border-t-0 border-r-0">
        <Link to={"/"}>
          {
            applicantFacingLogo ? (
              <img
                src={applicantFacingLogo}
                width={applicantFacingLogoWidth}
                style={{ maxHeight: 30 }}
                alt={programName}
              />
            ) : (
              <AidKitLogo width={60} height={30} />
            )
          }
        </Link>
        <h2 className="text-sm m-0">{programName}</h2>
      </div>

      <h2>Import</h2>
      { previewing ?
        <div className="p-10">
          <div>
            <div className="mt-3 text-center sm:mt-5">
              <h3>
                Import Preview
              </h3>
              <div className="mt-2">
                <Switch
                  label='Show mapping'
                  checked={showMapsToHeader}
                  onCheckedChange={() => setShowMapsToHeader(!showMapsToHeader)} />
              </div>
            </div>
            {formattedData.length > 0 && <div className="mt-4 overflow-auto">
              <h4 className="text-center mb-3">Applicants</h4>
              <p className="text-sm text-center text-gray-500">
                You are about to {config.mode.kind === 'Create' ? 'create' : 'update'} <strong>{formattedData.length}</strong> record(s) in the <strong>{programName}</strong> program. Review the data below.
              </p>
              <table className="min-w-full bg-white border">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 tracking-wider border-r-2">Row #</th>
                    {formattedColumns.map((column, index) => (
                      <th key={'csv' + index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 tracking-wider">
                        {column.csvColumn}
                        {column.encryptThisValue ? ' (will be encrypted)' : ''}
                        {(column.criteria === 'exists' || (config.mode.kind === 'Create' && config.mode.hashedFromColumn === column.csvColumn)) ? ' (Required)' : ''}
                        {column.unique ? ' (Unique)' : ''}
                      </th>
                    ))}
                  </tr>
                  {showMapsToHeader && (
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 lowercase tracking-wider border-r-2"></th>
                      {formattedColumns.map((column, index) => (
                        <th key={'targetField' + index} className="px-4 py-2 text-left text-xs font-medium text-gray-500 lowercase tracking-wider">
                          {column.targetField}
                          {config.mode.kind === 'Create' && column.targetField === config.mode.nameColumn ? ' (name column)' : ''}
                          {config.mode.kind === 'Update' && column.targetField === config.mode.uidColumn ? ' (uid column)' : ''}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {formattedData.map((row, rowIndex) => (
                    <tr key={rowIndex} className={`${errors[rowIndex] ? 'bg-red-100' : 'bg-white'}`}>
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r-2 ${errors[rowIndex] ? 'border-gray-300' : 'border-gray-200'}`}>
                        {rowIndex}
                      </td>
                      {formattedColumns.map((column, colIndex) => (
                        <td key={colIndex} className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700 ${errors[rowIndex]?.[colIndex] ? 'outline outline-offset-[-4px] outline-red-500' : ''}`}>
                          {row[column.csvColumn]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}

            {config.mode.kind === 'Create' && missingOrgNameRows.length > 0 && (
              <div className="mt-3">
                <div className="rounded-md p-4 mx-auto w-fit text-sm bg-yellow-100 text-yellow-800">
                  {`Warning: Missing values in the column "${config.mode.orgNameColumn}" in rows: ${missingOrgNameRows.join(", ")}`}
                </div>
              </div>
            )}

            {config.mode.kind === 'Create' && uniqueOrgNames.length > 0 && (
              <div className="mt-8">
                <div className="text-center">
                  <h4 className="mb-3">Organizations</h4>
                  <p className="text-sm text-gray-500">
                    You are about to create <strong>{uniqueOrgNames.length}</strong> organization(s) (if they don't already exist) in the <strong>{programName}</strong> program.
                  </p>
                </div>
                <table className="min-w-full bg-white border">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-500 tracking-wider border-r-2">Row #</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 tracking-wider">
                        Name
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {uniqueOrgNames.map((row, rowIndex) => (
                      <tr key={rowIndex} className={`${errors[rowIndex] ? 'bg-red-100' : 'bg-white'}`}>
                        <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r-2 ${errors[rowIndex] ? 'border-gray-300' : 'border-gray-200'}`}>
                          {rowIndex}
                        </td>
                        <td key={row} className={`px-4 py-2 whitespace-nowrap text-sm text-gray-700`}>
                          {row}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(hasErrors || serverValidationError) && <div className="mt-4">Fix the following errors in your csv to proceed:</div>}

            {hasErrors && (
              <div className="text-red-500">
                <ul>
                  {Object.keys(errors).map((key, i) =>
                    Object.keys(errors[key]).map((err, j) =>
                      <li key={key + i + j}>{errors[key][err]}</li>
                    )
                  )}
                </ul>
              </div>
            )}
            <div className="text-red-500 whitespace-pre-line">{serverValidationError}</div>
          </div>
          <div className="sm:flex sm:flex-row sm:items-center gap-3">
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={() => {
                setPreviewing(false)
                setServerValidationError('')
                setTaskIds([])
                taskIdToRowsRef.current = {}
                setTotalRowCount(0)
                setCompletedRowCount(0)
                setFailedRows([])
                setImporting(false)
              }}
            >
              Cancel
            </button>
            {failedRows.length > 0 && !importing && (
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-red-300 bg-white px-4 py-2 text-base font-medium text-red-700 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                onClick={downloadFailedRows}
              >
                Download failed rows ({failedRows.length})
              </button>
            )}
            <button
              type="button"
              className={`mt-2 ${hasErrors ? "opacity-50 cursor-not-allowed" : ""} mt-3 inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm`}
              onClick={doImport}
              disabled={hasErrors}
            >
              {importing
                ? <><SpacedSpinner />{totalRowCount > 0 && ` ${completedRowCount} / ${totalRowCount} rows complete`}</>
                : 'Import Data'}
            </button>
          </div>
        </div>
        :
        <div>
          <ul role="list" className="space-y-4">
            <li>
              <h3>Choose File</h3>
              <label htmlFor="file-upload">
                <span className={BUTTON_CLASS}>Upload a CSV file</span>
                <input onChange={handleFileUpload} id="file-upload" name="file-upload" type="file" className="sr-only" />
              </label>
              <span className="px-3">
                {!!file && <span>Chosen file: {<strong>{file.name}</strong>}</span>}
              </span>
            </li>
            {(showDistroForNonAdmins || (configContext.roles || []).includes('admin')) && <><li hidden={!data?.length} className="space-y-2 border p-2 rounded-md w-max max-w-full">
              <details className="max-w-xl" open={!!(groupByField || nameField)}>
                <summary>Grouping Options</summary>
                <div className="flex flex-col gap-2">
                  <div>
                    <div className="mt-2 flex rounded-md shadow-sm">
                      <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 px-3 text-gray-700 sm:text-sm">Group By Field:</span>
                      <select className="block text-center w-full flex-1 rounded-none rounded-r-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 sm:min-w-[20em]"
                        value={groupByField || undefined}
                        onChange={(e) => setGroupByField(e.target.value)}>
                        <option>Select...</option>
                        {Object.keys(data?.[0] ?? {}).map((col, i) => <option key={col + i} value={col}>{col}</option>)}
                      </select>
                    </div>
                    <div className="text-xs px-2 mt-1">When selected, every row that shares this value will be collapsed and grouped into a single row. Each of the those constituent rows will be represented as a nested JSON in a new column named <code>Records</code>.</div>
                  </div>

                  <div>
                    <div className="mt-2 flex rounded-md shadow-sm">
                      <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 px-[27px] text-gray-700 sm:text-sm">Name Field:</span>
                      <select className="block text-center w-full flex-1 rounded-none rounded-r-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 sm:min-w-[20em]"
                        value={nameField || undefined}
                        onChange={e => setNameField(e.target.value)}>
                        <option>Select...</option>
                        {Object.keys(data?.[0] ?? {}).map((col, i) => <option key={col + i} value={col}>{col}</option>)}
                      </select>
                    </div>
                    <div className="text-xs px-2 mt-1">When used with "Group By Field" field above, this field is used to determine the name value for each collapsed row formed by the grouping process. It will always take the name field value from the first constituent row.</div>
                  </div>

                </div>
                <button hidden={!grouped} className="rounded-md bg-red-400 ml-2 p-2" onClick={() => {
                  setGroupByField(null);
                  setNameField(null);
                  setGrouped(false);
                }}>
                  Reset
                </button>
              </details>

            </li>
            <li className="w-max rounded-md">
              <div className="mt-2 flex rounded-md shadow-sm">
                <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 py-2 px-2.5 text-gray-700 sm:text-sm">Add All Columns:</span>
                <input className="block cursor-pointer rounded-md min-w-[2em] border-0 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" onChange={e => {
                  if (!e.target.checked || !config.columns?.length || window.confirm('This will overwrite your existing mapping configuration, are you sure you wish to proceed?')) {
                    setAutoColumns(e.target.checked);
                  }
                }} type="checkbox" checked={autoColumns} />
              </div>
            </li></>}
          </ul>
          {(data || initialHash.config) && (
            <>
              <div hidden={autoColumns}>
                <h3>Columns in CSV</h3>
                <ul>
                  {data && data.length > 0 && Object.keys(data[0]).map((k) => <li key={k}>{k}</li>)}
                </ul>
              </div>
              {(showDistroForNonAdmins || (configContext.roles || []).includes('admin')) && <><h3>Create vs Update</h3>
                <span>For the "mode", select "Create" if you want to create new applicants from this data, or use "Update" if you want to change data for existing applicants</span>
                <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <Suspense>
                    <Distro
                      ref={distroRef}
                      value={config}
                      types='src/survey.ts'
                      name='CSVMapping'
                      onChange={(v) => {
                        setConfig(v);
                        if (!refsLoaded.current && data && data.length > 0) {
                          refsLoaded.current = true;
                          distroRef.current?.initialize(v, undefined, undefined, undefined, {
                            'csvColumns': Object.keys(data[0])
                          });
                        }
                      }} />
                  </Suspense>
                </div></>}
              <div className="mt-3 text-red-500">{invalidMessage}</div>
              <button
                onClick={() => setPreviewing(true)}
                type="button"
                className={`${invalidMessage ? "opacity-50 cursor-not-allowed" : ""} ${BUTTON_CLASS}`}
                disabled={importing || invalidMessage.length > 0}
              >
                Preview Import
              </button>
            </>
          )}
          {(configContext.roles || []).includes('admin') &&
                <div>
                  <hr className="mt-8" />
                  <h3>Create non-admin import link</h3>
                  <div><p>This tool creates an import link for non-admins to use, with the validation configured above.</p>
                    <p>Using this link, users with the 'can_import' role will be able to use this page to import values from CSVs, limited
                      only to the provided allowed fields as columns.
                      The allowed fields should be added as comma separated values.</p>
                  </div>
                  <div className="mt-4 mb-4 inline-flex">
                    <span className="font-semibold mr-2">Allowed Fields:</span>
                    <textarea
                      className="px-1 w-[500px]"
                      placeholder='phone_number, email, ...'
                      onChange={e => setAllowedFields(e.target.value)}></textarea>
                  </div>
                  <div>
                    <Switch
                      label='Allow validation & formatting updates'
                      checked={showDistroForNonAdmins}
                      onCheckedChange={() => setShowDistroForNonAdmins(!showDistroForNonAdmins)} />
                  </div>
                  <div>
                    <button
                      type="button"
                      className={BUTTON_CLASS}
                      onClick={handleGenerateLink}>
                      Generate link
                    </button>
                  </div>
                  {generatedImportUrl &&
                        <div className="mt-4 mr-4">
                          <br/>
                          <h5 className="font-semibold">Import URL: </h5>
                          <span style={{ overflowWrap: 'break-word' }}><a href={generatedImportUrl}>{generatedImportUrl}</a></span>
                        </div>}
                  <Modal
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Generated Import URL"
                    size="lg"
                  >
                    <p className="mb-4">
                      You can share this link with users who have administrative login access:
                    </p>
                    <div className="max-h-24 overflow-y-auto p-2 border border-gray-200 rounded bg-gray-50">
                      <a
                        href={generatedImportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline ml-2 break-words block max-w-full"
                      >
                        {generatedImportUrl}
                      </a>
                    </div>
                    <div className="h-6 flex items-center justify-center mt-4">
                      <p className={`text-sm text-gray-500 text-center transition-opacity duration-300 ${isCopied ? 'opacity-100' : 'opacity-0'}`}>
                        The link has been copied to your clipboard.
                      </p>
                    </div>
                    <div className="flex justify-center mt-2">
                      <DesignSystemButton
                        variant="primary"
                        onClick={handleCopyToClipboard}
                        size="lg"
                      >
                        Copy Link
                      </DesignSystemButton>
                    </div>
                  </Modal>
                </div>
          }
        </div>}
    </div>
}
